import { artifactQueries } from "@/hooks/artifactQueries.ts";
import type { GetArtifactResp, SubmitFormReq, SubmitFormResp } from "@/types/artifacts.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type ApiPath } from "@/lib/http.ts";
import { messageQueries } from "@/shared/messageQueries.ts";
import { useEffect, useRef, useState } from "react";
import { carryPairedApproval } from "@/shared/toolCallApproval.ts";

const getSubmitFormPath = (threadId: number, messageId: number, artifactId: string): ApiPath =>
  `/api/a/threads/${threadId}/messages/${messageId}/artifacts/${artifactId}/submitForm`;

/**
 * Epoch ms for an artifact's last write, or 0 when it carries no timestamp —
 * an untimestamped copy sorts oldest, so it never wins over one we can date.
 */
const updatedAtMs = (artifact: GetArtifactResp | undefined): number => {
  const updatedAt = artifact && "updatedAt" in artifact ? artifact.updatedAt : undefined;
  const ms = updatedAt ? new Date(updatedAt).getTime() : Number.NaN;
  return Number.isFinite(ms) ? ms : 0;
};

/**
 * Consecutive failed polls a card tolerates before it stops asking.
 *
 * The poll is the only way this hook ever learns an artifact settled, so a
 * detail request that keeps failing has no exit of its own: the artifact stays
 * at `inProgress`, `shouldPoll` stays true, and the request repeats at 1Hz for
 * as long as the card is mounted. Stop instead — whatever made the artifact
 * unreachable is not something an identical request will fix, and a card that
 * has given up is strictly better than one generating load forever.
 *
 * Paired with `retry: false` below, this is also the request budget: one
 * request per tick, so a wedged card costs this many in total.
 */
export const MAX_CONSECUTIVE_POLL_FAILURES = 5;

const shouldPoll = (artifact: GetArtifactResp) => {
  // A userResponse reply is never polled — it's driven by the sockets
  if (artifact.artifactType === "userResponse") return false;
  if (artifact.artifactType === "phoneCallDraft") {
    return (
      artifact.status === "inProgress" ||
      artifact.status === "queued" ||
      artifact.status === "ringing"
    );
  }
  if (artifact.artifactType === "oauthConnection") {
    return ["waitingForUserAction", "completing", "cancelling"].includes(artifact.status);
  }
  return "status" in artifact && artifact.status === "inProgress";
};

export const useArtifact = ({
  threadId,
  messageId,
  artifact,
}: {
  threadId: number;
  messageId: number;
  artifact: GetArtifactResp;
}) => {
  const queryClient = useQueryClient();
  const [shouldFetch, setShouldFetch] = useState(shouldPoll(artifact));
  // `errorUpdateCount` as of the last poll that succeeded, so the failures since
  // then can be counted without keeping a tally of our own. Held in a ref rather
  // than derived in an effect so the interval below reads it in the same tick
  // the failure lands — an effect updates a render too late, letting the poll
  // outlive its own cap.
  const errorsAtLastSuccess = useRef(0);

  // Use React Query with conditional fetching and polling
  const { data, error, isLoading } = useQuery({
    ...artifactQueries.detail(threadId, messageId, artifact.id),
    // Drive the interval off the query's own state, never the `artifact` prop.
    // The prop is a thread-snapshot copy, and an artifact that has become
    // unaddressable drops out of the thread read as well — so the prop can
    // freeze at `inProgress` with nothing left able to refresh it. The poll's
    // own response is the freshest status this hook can see.
    refetchInterval: (query) => {
      const { status, errorUpdateCount } = query.state;
      // Rebasing on every non-error settle is what makes the cap count
      // *consecutive* failures: a long poll has to survive unrelated blips
      // spread across it. `status` is the tiebreak rather than the
      // `dataUpdatedAt`/`errorUpdatedAt` stamps, which can land on the same
      // millisecond — `initialData` stamps the mount, so a fast first failure
      // ties with it and would be miscredited as a success. Re-running on
      // unchanged state rebases to the same value, so the observer may call
      // this as often as it likes.
      if (status !== "error") errorsAtLastSuccess.current = errorUpdateCount;
      if (errorUpdateCount - errorsAtLastSuccess.current >= MAX_CONSECUTIVE_POLL_FAILURES) {
        return false;
      }
      return shouldPoll(query.state.data ?? artifact) ? 1000 : false;
    },
    // The poll is its own retry, and a better one: the next tick is a second
    // away while the shared policy's backoff is already past that on its first
    // attempt. Leaving retries on would quadruple every tick (three retries per
    // 5xx) and stretch a cycle beyond the interval it is supposed to keep.
    retry: false,
    enabled: shouldFetch,
    initialData: artifact,
    // Keep the data fresh by marking it as stale immediately
    staleTime: 0,
  });

  // Update shouldFetch when artifact changes
  useEffect(() => {
    const needsFetch = shouldPoll(artifact);
    if (needsFetch && !shouldFetch) {
      setShouldFetch(true);
    }
  }, [artifact, shouldFetch]);

  // When the artifact prop changes (e.g., from a socket-triggered refetch),
  // update the query cache so useQuery returns the fresh data.
  // Without this, initialData is stale and the query won't refetch when disabled.
  //
  // The prop is a thread-snapshot copy, and a field edit is written to this
  // cache — never back into the snapshot. So the prop can be older than what
  // the cache already holds (a card mounting with a pre-edit snapshot after the
  // user edited it elsewhere). Seeding from it would roll the edit back on every
  // surface reading this key.
  //
  // Take the prop only when it is provably newer, so an artifact whose stamps
  // tie — or that carries none at all — keeps the cached copy instead of
  // reverting to the snapshot. Losing the user's edit is the worse failure, and
  // a genuine server update still reaches this cache through the query itself.
  useEffect(() => {
    queryClient.setQueryData<GetArtifactResp>(
      artifactQueries.detail(threadId, messageId, artifact.id).queryKey,
      (cached) => (cached && updatedAtMs(cached) >= updatedAtMs(artifact) ? cached : artifact),
    );
  }, [artifact, threadId, messageId, queryClient]);

  // A paired tool call keeps the buttons the feed gave it: the detail fetch
  // has no sibling form to read them from.
  const currentData = carryPairedApproval(data ?? artifact, artifact);
  // Stop polling when artifact completes
  const previousShouldPollRef = useRef(shouldPoll(currentData));
  useEffect(() => {
    const currentShouldPoll = shouldPoll(currentData);
    if (previousShouldPollRef.current && !currentShouldPoll) {
      // Artifact just finished - invalidate thread to update UI
      queryClient.invalidateQueries({
        queryKey: ["threads", threadId],
      });
    }
    previousShouldPollRef.current = currentShouldPoll;
  }, [currentData, threadId, queryClient]);

  return {
    artifact: currentData,
    isLoading,
    error,
  };
};

export const useArtifacts = ({ threadId, messageId }: { threadId: number; messageId: number }) => {
  const { data, error, isLoading } = useQuery(artifactQueries.list(threadId, messageId));
  return {
    artifactsResponse: data,
    isLoading,
    error,
  };
};

export const useSubmitForm = ({ threadId, messageId }: { threadId: number; messageId: number }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      artifactId,
      values,
    }: {
      artifactId: string;
      values: Record<string, string>;
    }) => {
      // Answering a form makes the server write the user message, same as any
      // other card decision — so it carries a correlation id for the same
      // reason `fireArtifactAction` mints one. Both surfaces that submit a form
      // (the canvas `AskUserForm` and the omnibox's browsed card) go through
      // here, and neither writes a local placeholder, so the id's whole job is
      // to mark the arriving message as this client's own.
      const requestData: SubmitFormReq = { values, tempClientId: Math.random() };
      const response = await api.post<SubmitFormResp>(
        getSubmitFormPath(threadId, messageId, artifactId),
        requestData,
      );
      return response.data;
    },
    // Answering a form decides that artifact, so refresh the feed it was read
    // from — otherwise every surface reading the thread still has it as an open
    // question: the omnibox overlay keeps offering the form the user just
    // submitted. The same refresh every other decision path does (BIG-7171).
    onSuccess: () => queryClient.invalidateQueries({ queryKey: messageQueries.thread(threadId) }),
  });
};
